import React, { useMemo, useState } from "react";
import { Paper, Typography, ToggleButtonGroup, ToggleButton, Box, alpha } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import EuroIcon from "@mui/icons-material/Euro";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import numeral from "numeral";
import { TopLevelPaper } from "../../../../../../../themes/StyledComponents";

export interface TopPayerRow {
  payerId: string;
  payerName: string;
  totalAmount: number;
  billCount: number;
  percentageOfTotalRevenue: number;
}

type SortMode = "amount" | "count";

interface Props {
  rows: TopPayerRow[];
}

/* palette */
const COLOR = {
  bg: "#F9F3EF",
  even: "#D2C1B6",
  header: "#456882",
  selected: "#1B3C53",
  secondary: "#97a3b1",
};

const fmtEuro = (n: number) => numeral(n).format("0,0.00 $");

const TopPayersPanel: React.FC<Props> = ({ rows }) => {
  const [mode, setMode] = useState<SortMode>("amount");

  const sorted = useMemo(() => {
    const key: keyof TopPayerRow = mode === "amount" ? "totalAmount" : "billCount";
    return [...rows].sort((a, b) => b[key] - a[key]);
  }, [rows, mode]);

  const PercentBar: React.FC<{ value: number }> = ({ value }) => (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: 8,
        borderRadius: 1,
        bgcolor: alpha("#ffffff", 0.8),
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          width: `${value}%`,
          bgcolor: "success.main",
          borderRadius: 1,
          transition: "width .6s",
        }}
      />
    </Box>
  );

  const columns: GridColDef[] = [
    {
      field: "rank",
      headerName: "No.",
      width: 60,
      sortable: false,
      headerAlign: "center",
      align: "center",
      valueGetter: (params) => sorted.findIndex((r) => r.payerId === params.row.payerId) + 1,
    },
    { field: "payerName", headerName: "Kostenträger", flex: 1, minWidth: 150 },
    {
      field: "billCount",
      headerName: "Rechnungen",
      width: 100,
      type: "number",
      headerAlign: "left",
      align: "left",
    },
    {
      field: "totalAmount",
      headerName: "Gesamt (€)",
      width: 130,
      type: "number",
      headerAlign: "left",
      align: "left",
      valueFormatter: ({ value }) => fmtEuro(value as number),
    },
    {
      field: "percentageOfTotalRevenue",
      headerName: "Gesamt (%)",
      width: 180,
      headerAlign: "left",
      align: "left",
      renderCell: ({ value }) => (
        <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption">{(value as number).toFixed(1)} %</Typography>
          <PercentBar value={value as number} />
        </Box>
      ),
    },
  ];

  return (
    <TopLevelPaper
      elevation={1}
      sx={{
        p: 2,
        borderRadius: 2,
        border: "0px",
      }}
    >
      {/* header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 0.5,
          alignItems: "center",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={450}
          color="text.primary"
        >
          Top Kostenträger
        </Typography>

        <ToggleButtonGroup
          size="small"
          value={mode}
          exclusive
          onChange={(_, v) => v && setMode(v)}
          sx={{
            "& .MuiToggleButton-root": {
              borderRadius: 1,
              color: COLOR.header,
              "&.Mui-selected": {
                bgcolor: COLOR.header,
                color: "#fff",
                "&:hover": { bgcolor: COLOR.header },
              },
            },
          }}
        >
          <ToggleButton value="amount">
            <EuroIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="count">
            <FormatListNumberedIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* grid */}
      <DataGrid
        rows={sorted}
        getRowId={(r) => r.payerId}
        columns={columns}
        density="compact"
        hideFooterSelectedRowCount
        hideFooter
        sx={{
          height: 200 /* fixed panel height */,
          borderRadius: 1,
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: COLOR.header,
            color: "#fff",
          },
          "& .MuiDataGrid-row.even": {
            bgcolor: COLOR.even,
          },
          /* custom scrollbar */
          "& .MuiDataGrid-virtualScroller": {
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-track": {
              bgcolor: alpha(COLOR.bg, 0.6),
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: COLOR.header,
              borderRadius: 3,
            },
          },
        }}
      />
    </TopLevelPaper>
  );
};

export default TopPayersPanel;
